import { useEffect, type ReactNode } from 'react'
import { useI18n } from '../i18n/useI18n'

/**
 * Panneau de saisie qui monte depuis le bas de l'ecran.
 *
 * Choisi plutot qu'une page separee : le pouce reste en bas, la liste
 * derriere garde le contexte, et fermer sans enregistrer est un geste
 * evident. Le bouton d'enregistrement est colle en bas, atteignable
 * sans faire defiler un formulaire long.
 */
export function Sheet({
  title,
  onClose,
  onSubmit,
  submitLabel,
  submitting,
  disabled,
  error,
  children,
}: {
  title: string
  onClose: () => void
  onSubmit: () => void
  submitLabel: string
  submitting?: boolean
  disabled?: boolean
  error?: string | null
  children: ReactNode
}) {
  const { t } = useI18n()

  // Empeche la page derriere de defiler pendant la saisie.
  useEffect(() => {
    const avant = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = avant
    }
  }, [])

  // Echap ferme, pour l'usage au clavier.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="sheet__backdrop" onClick={onClose}>
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sheet__head">
          <h2 className="sheet__title">{title}</h2>
          <button
            type="button"
            className="sheet__close"
            aria-label={t('cancel')}
            onClick={onClose}
          >
            ✕
          </button>
        </header>

        <form
          className="sheet__form"
          onSubmit={(e) => {
            e.preventDefault()
            onSubmit()
          }}
        >
          {/* Le corps defile, le pied reste. Le pied etait d'abord
              colle a l'interieur de la zone defilante et recouvrait le
              dernier champ. */}
          <div className="sheet__body">
            {children}
            {error && <p className="alert alert--error">{error}</p>}
          </div>

          <div className="sheet__foot">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={onClose}
              disabled={submitting}
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={submitting || disabled}
            >
              {submitting ? t('saving') : submitLabel}
            </button>
          </div>
        </form>

      </div>
    </div>
  )
}
